import React from 'react';
import { translate } from 'react-i18next';
import { Button } from 'react-bootstrap';
import FieldGroup from '../field_group/FieldGroup';
import Form from '../form/Form';

/**
 * PolygonInputForm
 * - Manage a list of 3D points (X, Y, Z) for shading devices
 * - For window: use 2D points (Z, Y) via axisLabels
 * - Enforce 3–20 points
 * - Optionally manage origin (x, y, z) like RectangularInputForm
 */
class PolygonInputForm extends React.Component {
  constructor(props) {
    super(props);
    // Default to 4 points (rectangle)
    const is3D = props.use3DVertices;
    const defaultPoints = is3D
      ? [{ x: 0, y: 0, z: 0 }, { x: 1, y: 0, z: 0 }, { x: 1, y: 1, z: 0 }, { x: 0, y: 1, z: 0 }]
      : [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];
    
    let initialPoints = defaultPoints;
    if (props.points && props.points.length >= 3) {
      // Ensure z value exists for 3D mode
      initialPoints = is3D
        ? props.points.map(p => ({ x: p.x || 0, y: p.y || 0, z: p.z || 0 }))
        : props.points;
    }
    
    this.state = {
      points: initialPoints,
      origin: props.origin || { x: 0, y: 0, z: 0 },
    };
  }

  componentDidMount() {
    this.notify();
  }

  notify() {
    const { name, onDataChanged, use3DVertices } = this.props;
    if (onDataChanged) {
      const data = {
        points: this.state.points.map(p => 
          use3DVertices
            ? { x: parseFloat(p.x || 0), y: parseFloat(p.y || 0), z: parseFloat(p.z || 0) }
            : { x: parseFloat(p.x || 0), y: parseFloat(p.y || 0) }
        )
      };
      if (this.props.origin !== undefined) {
        data.origin = {
          x: parseFloat(this.state.origin.x || 0),
          y: parseFloat(this.state.origin.y || 0),
          z: parseFloat(this.state.origin.z || 0),
        };
      }
      onDataChanged(name, data);
    }
  }

  updateOrigin(key, value) {
    const origin = { ...this.state.origin, [key]: value };
    this.setState({ origin }, () => this.notify());
  }

  addPoint() {
    if (this.state.points.length >= 20) return;
    const { use3DVertices } = this.props;
    const newPoint = use3DVertices ? { x: 0, y: 0, z: 0 } : { x: 0, y: 0 };
    this.setState(
      { points: [...this.state.points, newPoint] },
      () => this.notify(),
    );
  }

  sortPointsByAngle() {
    const { points } = this.state;
    if (!points || points.length < 3) return;
    const numeric = p => ({
      x: parseFloat(p.x || 0),
      y: parseFloat(p.y || 0),
      z: parseFloat(p.z || 0),
    });
    const converted = points.map(numeric);
    const cx = converted.reduce((s, p) => s + p.x, 0) / converted.length;
    const cy = converted.reduce((s, p) => s + p.y, 0) / converted.length;
    const angle = p => Math.atan2(p.y - cy, p.x - cx);
    const sorted = converted
      .map((p, idx) => ({ ...points[idx], _angle: angle(p), _dist: Math.hypot(p.x - cx, p.y - cy) }))
      .sort((a, b) => a._angle - b._angle || a._dist - b._dist)
      .map(({ _angle, _dist, ...rest }) => rest);
    this.setState({ points: sorted }, () => this.notify());
  }

  removePoint(index) {
    if (this.state.points.length <= 3) return;
    this.setState(
      { points: this.state.points.filter((p, i) => i !== index) },
      () => this.notify(),
    );
  }

  updatePoint(index, key, value) {
    const points = this.state.points.slice();
    points[index] = { ...points[index], [key]: value };
    this.setState({ points }, () => this.notify());
  }

  renderPointRow(p, index, t) {
    const { axisLabels, use3DVertices } = this.props;
    const xLabel = axisLabels && axisLabels.x ? axisLabels.x : 'X';
    const yLabel = axisLabels && axisLabels.y ? axisLabels.y : 'Y';
    const zLabel = axisLabels && axisLabels.z ? axisLabels.z : 'Z';
    
    return (
      <div
        key={`pt-${index}`}
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 12,
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <label htmlFor={`poly-x-${index}`}>{`${xLabel} (${t('meter abbrv')})`}</label>
            <input
              id={`poly-x-${index}`}
              type="number"
              value={p.x}
              onChange={e => this.updatePoint(index, 'x', e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <label htmlFor={`poly-y-${index}`}>{`${yLabel} (${t('meter abbrv')})`}</label>
            <input
              id={`poly-y-${index}`}
              type="number"
              value={p.y}
              onChange={e => this.updatePoint(index, 'y', e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          {use3DVertices && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <label htmlFor={`poly-z-${index}`}>{`${zLabel} (${t('meter abbrv')})`}</label>
              <input
                id={`poly-z-${index}`}
                type="number"
                value={p.z}
                onChange={e => this.updatePoint(index, 'z', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>
        <Button
          bsStyle="danger"
          onClick={() => this.removePoint(index)}
          disabled={this.state.points.length <= 3}
        >
          {t('delete')}
        </Button>
      </div>
    );
  }

  renderOriginInputs(t) {
    const { origin } = this.state;
    return (
      <div style={{ marginBottom: 16 }}>
        <h5 style={{ marginBottom: 8 }}>{t('position') || 'Position'}</h5>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label>{`X (${t('meter abbrv')})`}</label>
            <input
              type="number"
              value={origin.x}
              onChange={e => this.updateOrigin('x', e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label>{`Y (${t('meter abbrv')})`}</label>
            <input
              type="number"
              value={origin.y}
              onChange={e => this.updateOrigin('y', e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label>{`Z (${t('meter abbrv')})`}</label>
            <input
              type="number"
              value={origin.z}
              onChange={e => this.updateOrigin('z', e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { t, origin } = this.props;
    const { points } = this.state;
    return (
      <div style={{ width: '100%' }}>
        {origin !== undefined && this.renderOriginInputs(t)}
        <h5 style={{ marginBottom: 8 }}>{t('vertices') || 'Vertices'}</h5>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {points.map((p, i) => this.renderPointRow(p, i, t))}
        </div>
        <div style={{ marginTop: 10 }}>
          <Button
            onClick={() => this.addPoint()}
            disabled={points.length >= 20}
          >
            {t('add point') || 'Add point'}
          </Button>
          <span style={{ marginLeft: 10, color: '#777' }}>
            {`${t('points') || 'Points'}: ${points.length} / 20`}
          </span>
        </div>
      </div>
    );
  }
}

PolygonInputForm.defaultProps = {
  name: 'polygon',
  points: [],
  onDataChanged: null,
  axisLabels: null,
  use3DVertices: false,
};

export default translate('translations')(PolygonInputForm);






// WEBPACK FOOTER //
// ./src/components/sc_editor/PolygonInputForm.js